/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames'; // eslint-disable-line import/named

import { EuiFormControlLayout, EuiValidatableControl } from '../form';
import { EuiErrorBoundary } from '../error_boundary';
import { EuiI18nConsumer } from '../context'; // @ts-ignore the type is provided by react-datepicker.d.ts

import { ReactDatePicker as _ReactDatePicker } from '../../../packages'; // eslint-disable-line import/no-unresolved

export var euiDatePickerDefaultDateFormat = 'MM/DD/YYYY';
export var euiDatePickerDefaultTimeFormat = 'hh:mm A';
var DatePicker = _ReactDatePicker;
export class EuiDatePicker extends Component {
  static defaultProps = {
    adjustDateOnChange: true,
    dateFormat: euiDatePickerDefaultDateFormat,
    fullWidth: false,
    inputRef: () => {},
    isLoading: false,
    shadow: true,
    shouldCloseOnSelect: true,
    showIcon: true,
    showTimeSelect: false,
    timeFormat: euiDatePickerDefaultTimeFormat,
    popoverPlacement: 'bottom-start'
  };

  render() {
    const {
      adjustDateOnChange,
      calendarClassName,
      className,
      customInput,
      dateFormat,
      dayClassName,
      disabled,
      excludeDates,
      filterDate,
      fullWidth,
      iconType,
      injectTimes,
      inline,
      inputRef,
      isInvalid,
      isLoading,
      locale,
      maxDate,
      maxTime,
      minDate,
      minTime,
      onChange,
      onClear,
      openToDate,
      placeholder,
      popperClassName,
      popoverPlacement,
      selected,
      shadow,
      shouldCloseOnSelect,
      showIcon,
      showTimeSelect,
      showTimeSelectOnly,
      timeFormat,
      utcOffset,
      ...rest
    } = this.props;
    const classes = classNames('euiDatePicker', {
      'euiDatePicker--shadow': shadow,
      'euiDatePicker--inline': inline
    });
    const datePickerClasses = classNames('euiDatePicker', 'euiFieldText', {
      'euiFieldText--fullWidth': fullWidth,
      'euiFieldText-isLoading': isLoading,
      'euiFieldText--withIcon': !inline && showIcon,
      'euiFieldText-isInvalid': isInvalid
    }, className);
    let optionalIcon;

    if (inline || customInput || !showIcon) {
      optionalIcon = undefined;
    } else if (iconType) {
      optionalIcon = iconType;
    } else if (showTimeSelectOnly) {
      optionalIcon = 'clock';
    } else {
      optionalIcon = 'calendar';
    } // In case the consumer did not alter the default date format but wants
    // to add the time select, we append the default time format


    let fullDateFormat = dateFormat;

    if (showTimeSelect && dateFormat === euiDatePickerDefaultDateFormat) {
      fullDateFormat = `${dateFormat} ${timeFormat}`;
    } // EuiDatePicker only supports a subset of props from react-datepicker. Using any of
    // the unsupported props below will spit out an error.


    const PropNotSupported = () => {
      throw new Error(`You are using a prop from react-datepicker that EuiDatePicker
        does not support. Please check the EUI documentation for more information.`);
    };

    if ( // We don't want to show multiple months next to each other
    this.props.monthsShown || // There is no need to show week numbers
    this.props.showWeekNumbers || // Our css adapts to height, no need to fix it
    this.props.fixedHeight || // We force the month / year selection UI. No need to configure it
    this.props.dropdownMode || // Short month is uncessary. Our UI has plenty of room for full months
    this.props.useShortMonthInDropdown || // The today button is not needed. This should always be external to the calendar
    this.props.todayButton || // We hide the time caption, so there is no need to overwrite its text
    this.props.timeCaption || // We always want keyboard accessibility on
    this.props.disabledKeyboardNavigation || // This is easy enough to do. It can conflict with isLoading state
    this.props.isClearable || // There is no reason to launch the datepicker in its own modal. Can always build these ourselves
    this.props.withPortal) {
      return <EuiErrorBoundary>
          <PropNotSupported />
        </EuiErrorBoundary>;
    }

    return <span>
        <span className={classes}>
          <EuiFormControlLayout icon={optionalIcon} fullWidth={fullWidth} clear={selected && onClear ? {
          onClick: onClear
        } : undefined} isLoading={isLoading}>
            <EuiValidatableControl isInvalid={isInvalid}>
              <EuiI18nConsumer>
                {({
                locale: contextLocale
              }) => {
                return <DatePicker adjustDateOnChange={adjustDateOnChange} calendarClassName={calendarClassName} className={datePickerClasses} customInput={customInput} dateFormat={fullDateFormat} dayClassName={dayClassName} disabled={disabled} excludeDates={excludeDates} filterDate={filterDate} injectTimes={injectTimes} inline={inline} locale={locale || contextLocale} maxDate={maxDate} maxTime={maxTime} minDate={minDate} minTime={minTime} onChange={onChange} openToDate={openToDate} placeholderText={placeholder} popperClassName={popperClassName} ref={inputRef} selected={selected} shouldCloseOnSelect={shouldCloseOnSelect} showMonthDropdown showTimeSelect={showTimeSelect} showTimeSelectOnly={showTimeSelectOnly} showYearDropdown timeFormat={timeFormat} utcOffset={utcOffset} yearDropdownItemNumber={7} accessibleMode popperPlacement={popoverPlacement} {...rest} />;
              }}
              </EuiI18nConsumer>
            </EuiValidatableControl>
          </EuiFormControlLayout>
        </span>
      </span>;
  }

}
EuiDatePicker.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Applies classes to the numbered days provided. Check docs for example.
     */
  dayClassName: PropTypes.func,

  /**
     * Makes the input full width
     */
  fullWidth: PropTypes.bool,

  /**
     * ref for the ReactDatePicker instance
     */
  inputRef: PropTypes.any.isRequired,

  /**
     * Provides styling to the input when invalid
     */
  isInvalid: PropTypes.bool,

  /**
     * Provides styling to the input when loading
     */
  isLoading: PropTypes.bool,

  /**
     * What to do when the input is cleared by the x icon
     */
  onClear: PropTypes.func,

  /**
     * Opens to this date (in moment format) on first press, regardless of selection
     */
  openToDate: PropTypes.any,

  /**
     * Shows only when no date is selected
     */
  placeholder: PropTypes.string,

  /**
     * Can turn the shadow off if using the inline prop
     */
  shadow: PropTypes.bool,

  /**
     * Show the icon in input
     */
  showIcon: PropTypes.bool,

  /**
     * Pass an icon type to change the default `calendar` or `clock` icon
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "annotation", "apmApp", "apmTrace", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "check", "checkInCircleFilled", "cheer", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "compute", "console", "consoleApp", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "currency", "cut", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentEdit", "documents", "dot", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "fullScreen", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "lensApp", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAPM", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuLeft", "menuRight", "merge", "metricbeatApp", "metricsApp", "minimize", "minusInCircle", "minusInCircleFilled", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "pin", "pinFilled", "pipelineApp", "play", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "reporter", "reportingApp", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "timeline", "timelionApp", "training", "trash", "upgradeAssistantApp", "uptimeApp", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired, PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "annotation", "apmApp", "apmTrace", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "check", "checkInCircleFilled", "cheer", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "compute", "console", "consoleApp", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "currency", "cut", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentEdit", "documents", "dot", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "fullScreen", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "lensApp", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAPM", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuLeft", "menuRight", "merge", "metricbeatApp", "metricsApp", "minimize", "minusInCircle", "minusInCircleFilled", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "pin", "pinFilled", "pipelineApp", "play", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "reporter", "reportingApp", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "timeline", "timelionApp", "training", "trash", "upgradeAssistantApp", "uptimeApp", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
    side: PropTypes.any,
    ref: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired])
  }).isRequired]),

  /**
     * Sets the placement of the popover. It accepts: `"bottom"`, `"bottom-end"`, `"bottom-start"`, `"left"`, `"left-end"`, `"right"`, `"right-end"`, `"right-start"`, `"top"`, `"top-end"`, `"top-start"`
     */
  popoverPlacement: PropTypes.any
};

try {
  // @ts-ignore
  EuiDatePicker.displayName = "EuiDatePicker"; // @ts-ignore

  EuiDatePicker.__docgenInfo = {
    "description": "",
    "displayName": "EuiDatePicker",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "Added to the actual input of the calendar",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "dayClassName": {
        "defaultValue": null,
        "description": "Applies classes to the numbered days provided. Check docs for example.",
        "name": "dayClassName",
        "required": false,
        "type": {
          "name": "((date: Moment) => string | null) | undefined"
        }
      },
      "fullWidth": {
        "defaultValue": {
          value: false
        },
        "description": "Makes the input full width",
        "name": "fullWidth",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "inputRef": {
        "defaultValue": {
          value: "() => {}"
        },
        "description": "ref for the ReactDatePicker instance",
        "name": "inputRef",
        "required": false,
        "type": {
          "name": "Ref<Component<ReactDatePickerProps, any, any>>"
        }
      },
      "isInvalid": {
        "defaultValue": null,
        "description": "Provides styling to the input when invalid",
        "name": "isInvalid",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isLoading": {
        "defaultValue": {
          value: false
        },
        "description": "Provides styling to the input when loading",
        "name": "isLoading",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "onClear": {
        "defaultValue": null,
        "description": "What to do when the input is cleared by the x icon",
        "name": "onClear",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined"
        }
      },
      "openToDate": {
        "defaultValue": null,
        "description": "Opens to this date (in moment format) on first press, regardless of selection",
        "name": "openToDate",
        "required": false,
        "type": {
          "name": "Moment | undefined"
        }
      },
      "placeholder": {
        "defaultValue": null,
        "description": "Shows only when no date is selected",
        "name": "placeholder",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "shadow": {
        "defaultValue": {
          value: true
        },
        "description": "Can turn the shadow off if using the inline prop",
        "name": "shadow",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showIcon": {
        "defaultValue": {
          value: true
        },
        "description": "Show the icon in input",
        "name": "showIcon",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "iconType": {
        "defaultValue": null,
        "description": "Pass an icon type to change the default `calendar` or `clock` icon",
        "name": "iconType",
        "required": false,
        "type": {
          "name": "string | ComponentClass<{}, any> | FunctionComponent<{}> | (Pick<CommonProps & DisambiguateSet<Pick<ButtonHTMLAttributes<HTMLButtonElement>, \"color\" | ... 262 more ... | \"formTarget\">, HTMLAttributes<...>> & HTMLAttributes<...> & { ...; }, \"color\" | ... 263 more ... | \"formTarget\"> & { ...; }) | (Pick<...> & { ...; ..."
        }
      },
      "popoverPlacement": {
        "defaultValue": {
          value: "bottom-start"
        },
        "description": "Sets the placement of the popover. It accepts: `\"bottom\"`, `\"bottom-end\"`, `\"bottom-start\"`, `\"left\"`, `\"left-end\"`, `\"right\"`, `\"right-end\"`, `\"right-start\"`, `\"top\"`, `\"top-end\"`, `\"top-start\"`",
        "name": "popoverPlacement",
        "required": false,
        "type": {
          "name": "\"left\" | \"right\" | \"top\" | \"bottom\" | \"bottom-start\" | \"bottom-end\" | \"left-end\" | \"right-end\" | \"right-start\" | \"top-end\" | \"top-start\" | undefined"
        }
      },
      "adjustDateOnChange": {
        "defaultValue": {
          value: true
        },
        "description": "Whether changes to Year and Month (via dropdowns) should trigger `onChange`",
        "name": "adjustDateOnChange",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "accessibleMode": {
        "defaultValue": null,
        "description": "",
        "name": "accessibleMode",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "allowSameDay": {
        "defaultValue": null,
        "description": "",
        "name": "allowSameDay",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "autoComplete": {
        "defaultValue": null,
        "description": "",
        "name": "autoComplete",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "autoFocus": {
        "defaultValue": null,
        "description": "",
        "name": "autoFocus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "calendarClassName": {
        "defaultValue": null,
        "description": "Optional class added to the calendar portion of datepicker",
        "name": "calendarClassName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "customInput": {
        "defaultValue": null,
        "description": "Replaces the input with any node, like a button",
        "name": "customInput",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "customInputRef": {
        "defaultValue": null,
        "description": "",
        "name": "customInputRef",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "dateFormat": {
        "defaultValue": null,
        "description": "Accepts any moment format string",
        "name": "dateFormat",
        "required": false,
        "type": {
          "name": "string | string[] | undefined"
        }
      },
      "dateFormatCalendar": {
        "defaultValue": null,
        "description": "",
        "name": "dateFormatCalendar",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "disabled": {
        "defaultValue": null,
        "description": "",
        "name": "disabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "disabledKeyboardNavigation": {
        "defaultValue": null,
        "description": "",
        "name": "disabledKeyboardNavigation",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "dropdownMode": {
        "defaultValue": null,
        "description": "",
        "name": "dropdownMode",
        "required": false,
        "type": {
          "name": "\"select\" | \"scroll\" | undefined"
        }
      },
      "endDate": {
        "defaultValue": null,
        "description": "",
        "name": "endDate",
        "required": false,
        "type": {
          "name": "Moment | null | undefined"
        }
      },
      "excludeDates": {
        "defaultValue": null,
        "description": "",
        "name": "excludeDates",
        "required": false,
        "type": {
          "name": "Moment[] | undefined"
        }
      },
      "excludeTimes": {
        "defaultValue": null,
        "description": "",
        "name": "excludeTimes",
        "required": false,
        "type": {
          "name": "Moment[] | undefined"
        }
      },
      "filterDate": {
        "defaultValue": null,
        "description": "",
        "name": "filterDate",
        "required": false,
        "type": {
          "name": "((date: Moment) => boolean) | undefined"
        }
      },
      "fixedHeight": {
        "defaultValue": null,
        "description": "",
        "name": "fixedHeight",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "forceShowMonthNavigation": {
        "defaultValue": null,
        "description": "",
        "name": "forceShowMonthNavigation",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "formatWeekNumber": {
        "defaultValue": null,
        "description": "",
        "name": "formatWeekNumber",
        "required": false,
        "type": {
          "name": "((date: Moment) => ReactText) | undefined"
        }
      },
      "highlightDates": {
        "defaultValue": null,
        "description": "",
        "name": "highlightDates",
        "required": false,
        "type": {
          "name": "Moment[] | undefined"
        }
      },
      "id": {
        "defaultValue": null,
        "description": "",
        "name": "id",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "includeDates": {
        "defaultValue": null,
        "description": "",
        "name": "includeDates",
        "required": false,
        "type": {
          "name": "Moment[] | undefined"
        }
      },
      "includeTimes": {
        "defaultValue": null,
        "description": "",
        "name": "includeTimes",
        "required": false,
        "type": {
          "name": "Moment[] | undefined"
        }
      },
      "inline": {
        "defaultValue": null,
        "description": "",
        "name": "inline",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "injectTimes": {
        "defaultValue": null,
        "description": "Adds additional times to the time selector other then :30 increments",
        "name": "injectTimes",
        "required": false,
        "type": {
          "name": "Moment[] | undefined"
        }
      },
      "isClearable": {
        "defaultValue": null,
        "description": "",
        "name": "isClearable",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "locale": {
        "defaultValue": null,
        "description": "Switches the locale / display. \"en-us\", \"zn-ch\"...etc",
        "name": "locale",
        "required": false,
        "type": {
          "name": "string | boolean | Moment | Duration | string[] | undefined"
        }
      },
      "maxDate": {
        "defaultValue": null,
        "description": "The max date accepted (in moment format) as a selection",
        "name": "maxDate",
        "required": false,
        "type": {
          "name": "Moment | undefined"
        }
      },
      "maxTime": {
        "defaultValue": null,
        "description": "The max time accepted (in moment format) as a selection",
        "name": "maxTime",
        "required": false,
        "type": {
          "name": "Moment | undefined"
        }
      },
      "minDate": {
        "defaultValue": null,
        "description": "The min date accepted (in moment format) as a selection",
        "name": "minDate",
        "required": false,
        "type": {
          "name": "Moment | undefined"
        }
      },
      "minTime": {
        "defaultValue": null,
        "description": "The min time accepted (in moment format) as a selection",
        "name": "minTime",
        "required": false,
        "type": {
          "name": "Moment | undefined"
        }
      },
      "monthsShown": {
        "defaultValue": null,
        "description": "",
        "name": "monthsShown",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "name": {
        "defaultValue": null,
        "description": "",
        "name": "name",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onBlur": {
        "defaultValue": null,
        "description": "",
        "name": "onBlur",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<HTMLInputElement>) => void) | undefined"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "What to do when the input changes",
        "name": "onChange",
        "required": false,
        "type": {
          "name": "((date: Moment | null, event?: SyntheticEvent<any, Event> | undefined) => void) | undefined"
        }
      },
      "onChangeRaw": {
        "defaultValue": null,
        "description": "",
        "name": "onChangeRaw",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<HTMLInputElement>) => void) | undefined"
        }
      },
      "onClickOutside": {
        "defaultValue": null,
        "description": "",
        "name": "onClickOutside",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<HTMLDivElement, MouseEvent>) => void) | undefined"
        }
      },
      "onFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onFocus",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<HTMLInputElement>) => void) | undefined"
        }
      },
      "onKeyDown": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyDown",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<HTMLDivElement>) => void) | undefined"
        }
      },
      "onMonthChange": {
        "defaultValue": null,
        "description": "",
        "name": "onMonthChange",
        "required": false,
        "type": {
          "name": "((date: Moment) => void) | undefined"
        }
      },
      "onSelect": {
        "defaultValue": null,
        "description": "",
        "name": "onSelect",
        "required": false,
        "type": {
          "name": "((date: Moment, event?: SyntheticEvent<any, Event> | undefined) => void) | undefined"
        }
      },
      "onWeekSelect": {
        "defaultValue": null,
        "description": "",
        "name": "onWeekSelect",
        "required": false,
        "type": {
          "name": "((firstDayOfWeek: Moment, weekNumber: ReactText, event?: SyntheticEvent<any, Event> | undefined) => void) | undefined"
        }
      },
      "onYearChange": {
        "defaultValue": null,
        "description": "",
        "name": "onYearChange",
        "required": false,
        "type": {
          "name": "((date: Moment) => void) | undefined"
        }
      },
      "peekNextMonth": {
        "defaultValue": null,
        "description": "",
        "name": "peekNextMonth",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "placeholderText": {
        "defaultValue": null,
        "description": "",
        "name": "placeholderText",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "popperClassName": {
        "defaultValue": null,
        "description": "Class applied to the popup, when inline is false",
        "name": "popperClassName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "popperContainer": {
        "defaultValue": null,
        "description": "",
        "name": "popperContainer",
        "required": false,
        "type": {
          "name": "((props: { children: ReactNode[]; }) => ReactNode) | undefined"
        }
      },
      "popperPlacement": {
        "defaultValue": null,
        "description": "",
        "name": "popperPlacement",
        "required": false,
        "type": {
          "name": "\"left\" | \"right\" | \"top\" | \"bottom\" | \"bottom-start\" | \"bottom-end\" | \"left-end\" | \"right-end\" | \"right-start\" | \"top-end\" | \"top-start\" | undefined"
        }
      },
      "preventOpenOnFocus": {
        "defaultValue": null,
        "description": "",
        "name": "preventOpenOnFocus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "readOnly": {
        "defaultValue": null,
        "description": "",
        "name": "readOnly",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "required": {
        "defaultValue": null,
        "description": "",
        "name": "required",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "scrollableMonthYearDropdown": {
        "defaultValue": null,
        "description": "",
        "name": "scrollableMonthYearDropdown",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "scrollableYearDropdown": {
        "defaultValue": null,
        "description": "",
        "name": "scrollableYearDropdown",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "selected": {
        "defaultValue": null,
        "description": "The selected datetime (in moment format)",
        "name": "selected",
        "required": false,
        "type": {
          "name": "Moment | null | undefined"
        }
      },
      "selectsEnd": {
        "defaultValue": null,
        "description": "",
        "name": "selectsEnd",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "selectsStart": {
        "defaultValue": null,
        "description": "",
        "name": "selectsStart",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "shouldCloseOnSelect": {
        "defaultValue": {
          value: true
        },
        "description": "Will close the popup on selection",
        "name": "shouldCloseOnSelect",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showDisabledMonthNavigation": {
        "defaultValue": null,
        "description": "",
        "name": "showDisabledMonthNavigation",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showMonthDropdown": {
        "defaultValue": null,
        "description": "",
        "name": "showMonthDropdown",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showMonthYearDropdown": {
        "defaultValue": null,
        "description": "",
        "name": "showMonthYearDropdown",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showTimeSelect": {
        "defaultValue": {
          value: false
        },
        "description": "Show the time selection alongside the calendar",
        "name": "showTimeSelect",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showTimeSelectOnly": {
        "defaultValue": null,
        "description": "Only show the time selector, not the calendar",
        "name": "showTimeSelectOnly",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showWeekNumbers": {
        "defaultValue": null,
        "description": "",
        "name": "showWeekNumbers",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showYearDropdown": {
        "defaultValue": null,
        "description": "",
        "name": "showYearDropdown",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "startDate": {
        "defaultValue": null,
        "description": "",
        "name": "startDate",
        "required": false,
        "type": {
          "name": "Moment | null | undefined"
        }
      },
      "startOpen": {
        "defaultValue": null,
        "description": "",
        "name": "startOpen",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "tabIndex": {
        "defaultValue": null,
        "description": "",
        "name": "tabIndex",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "timeCaption": {
        "defaultValue": null,
        "description": "",
        "name": "timeCaption",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "timeFormat": {
        "defaultValue": null,
        "description": "The format of the time within the selector, in moment notation",
        "name": "timeFormat",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "timeIntervals": {
        "defaultValue": null,
        "description": "",
        "name": "timeIntervals",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "title": {
        "defaultValue": null,
        "description": "",
        "name": "title",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "todayButton": {
        "defaultValue": null,
        "description": "",
        "name": "todayButton",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "useShortMonthInDropdown": {
        "defaultValue": null,
        "description": "",
        "name": "useShortMonthInDropdown",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "useWeekdaysShort": {
        "defaultValue": null,
        "description": "",
        "name": "useWeekdaysShort",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "utcOffset": {
        "defaultValue": null,
        "description": "",
        "name": "utcOffset",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "value": {
        "defaultValue": null,
        "description": "",
        "name": "value",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "weekLabel": {
        "defaultValue": null,
        "description": "",
        "name": "weekLabel",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "withPortal": {
        "defaultValue": null,
        "description": "",
        "name": "withPortal",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "yearDropdownItemNumber": {
        "defaultValue": null,
        "description": "",
        "name": "yearDropdownItemNumber",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/date_picker/date_picker.tsx#EuiDatePicker"] = {
      docgenInfo: EuiDatePicker.__docgenInfo,
      name: "EuiDatePicker",
      path: "src/components/date_picker/date_picker.tsx#EuiDatePicker"
    };
} catch (__react_docgen_typescript_loader_error) {}